// ProgressHeader - Header con información de progreso de la auditoría
// Sprint 3 - Mobile-first design

import React from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  LinearProgress,
  Tooltip,
  useTheme,
  useMediaQuery,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';

interface ProgressHeaderProps {
  negocioNombre: string;
  formularioNombre: string;
  progreso: number; // 0-100
  questionsAnswered: number;
  totalQuestions: number;
  onBack: () => void;
  onSave: () => void;
  saving: boolean;
  seccionActual?: {
    nombre: string;
    color?: string;
  };
}

const ProgressHeader: React.FC<ProgressHeaderProps> = ({
  negocioNombre,
  formularioNombre,
  progreso,
  questionsAnswered,
  totalQuestions,
  onBack,
  onSave,
  saving,
  seccionActual,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <AppBar 
      position="fixed" 
      elevation={2}
      className="audit-progress-header"
      sx={{
        // Asegurar que ocupe el ancho completo sin el offset del Layout
        // En desktop, dejar espacio para el drawer (240px)
        left: { xs: 0, md: '240px' },
        right: 0,
        width: { xs: '100vw', md: 'calc(100vw - 240px)' },
        maxWidth: { xs: '100vw', md: 'calc(100vw - 240px)' },
        top: 0, // CSS override controlará esto
        zIndex: 1200, // Por encima del drawer (1100) y del header del Layout (1100)
      }}
    >
      <Toolbar sx={{ 
        gap: { xs: 0.5, sm: 1 }, 
        minHeight: { xs: 52, sm: 64 }, 
        px: { xs: 0.5, sm: 2 },
        alignItems: 'center',
      }}>
        {/* Botón volver */}
        <Tooltip title="Volver">
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ 
              mr: { xs: 0.5, sm: 2 },
              ml: { xs: 0.5, sm: 0 },
              p: { xs: 1.25, sm: 1 },
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
              '&:active': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
              }
            }}
          >
            <ArrowBackIcon sx={{ fontSize: { xs: 30, sm: 28 } }} />
          </IconButton>
        </Tooltip>

        {/* Información de la auditoría */}
        <Box sx={{ 
          flex: 1, 
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}>
          {/* Nombre del negocio */}
          <Box
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.9rem' },
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.3,
            }}
          >
            {negocioNombre}
          </Box>

          {/* Nombre del formulario + Sección actual inline */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              fontSize: { xs: '0.65rem', sm: '0.8rem' },
              opacity: 0.9,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              lineHeight: 1.3,
            }}
          >
            <Box
              component="span"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {formularioNombre}
            </Box>
            
            {/* Sección actual con indicador de color (inline) */}
            {seccionActual && seccionActual.nombre && (
              <>
                <Box component="span" sx={{ opacity: 0.5 }}>•</Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexShrink: 0,
                  }}
                >
                  {seccionActual.color && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        bgcolor: seccionActual.color,
                        borderRadius: '50%',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: { xs: '0.65rem', sm: '0.8rem' },
                      fontWeight: 700,
                      color: '#FFEB5D',
                      lineHeight: 1.3,
                    }}
                  >
                    {seccionActual.nombre}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Contador de preguntas (desktop) */}
          {!isMobile && (
            <Box
              sx={{
                fontSize: '0.75rem',
                opacity: 0.8,
                mt: 0.5,
              }}
            >
              {questionsAnswered} de {totalQuestions} preguntas ({progreso}%)
            </Box>
          )}
        </Box>

        {/* Botón guardar manual - DESTACADO */}
        <Tooltip title={saving ? "Guardando..." : "Guardar progreso"}>
          <IconButton
            color="inherit"
            onClick={onSave}
            disabled={saving}
            size="small"
            sx={{
              bgcolor: 'success.main',
              color: 'white',
              border: '2px solid',
              borderColor: 'success.light',
              p: 0.75,
              width: 36,
              height: 36,
              mr: { xs: 0, sm: 0 },
              '&:hover': {
                bgcolor: 'success.dark',
                transform: 'scale(1.05)',
              },
              '&:disabled': {
                bgcolor: 'action.disabledBackground',
                borderColor: 'action.disabled',
              },
            }}
          >
            <SaveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Toolbar>

      {/* Barra de progreso */}
      <LinearProgress
        variant="determinate"
        value={progreso}
        sx={{
          height: 3,
          bgcolor: 'rgba(255, 255, 255, 0.2)',
          '& .MuiLinearProgress-bar': {
            bgcolor: 'success.main',
          },
        }}
      />
    </AppBar>
  );
};

export default ProgressHeader;
